import InfoIcon from "@mui/icons-material/Info";
import { IconButton, Paper, Tooltip } from "@mui/material";
import { LastUpdatedBy } from "@rubi/api-generated";

import LexicalEditor from "@/common/LexicalEditor";
import { formatDateShort } from "@/common/utils";

import cardStyles from "./CondensedReportView.module.scss";
import styles from "./EmailNotesCard.module.scss";
import SectionHeader from "./SectionHeader";

const EMAIL_NOTE_TOOLTIP =
  "Sent email response automatically included personalized greeting, reporting tracking number, incident number, and officer details.";

// The escalation card historically used "response template" rather than
// "email response"; kept verbatim so the extraction doesn't change copy.
const ESCALATION_NOTE_TOOLTIP =
  "Sent response template automatically included personalized greeting, reporting tracking number, incident number, and officer details.";

/** A note attached to an outcome email, rendered as a report-style section. */
const NoteSection = ({
  title,
  markdown,
  tooltip,
}: {
  title: string;
  markdown: string;
  tooltip: string;
}) => (
  <section className={styles.section}>
    <SectionHeader
      title={title}
      className={cardStyles.officerCardHeader}
      actions={
        <Tooltip title={tooltip}>
          <IconButton size="small" className={styles.infoButton}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    />
    <div className={styles.noteBody}>
      <LexicalEditor markdown={markdown} readOnly={true} />
    </div>
  </section>
);

export interface EmailNotesCardProps {
  /** Whether the report is in a closed (approved/rejected/escalated) state. */
  isClosed: boolean;
  /** Current report status, e.g. "approved" | "rejected" | "escalated". */
  status: string;
  /** Notes attached to the outcome email, keyed by action. */
  notes: {
    approve: string;
    reject: string;
    escalate: string;
  };
  /** Reviewer who last acted on the report — shown in the closed-state footer. */
  lastUpdatedBy?: LastUpdatedBy | null;
  /** ISO timestamp of the last update — shown in the closed-state footer. */
  timeUpdated?: string | null;
}

/**
 * The card on the condensed report view that shows the note attached to the
 * outcome email (approval / rejection / escalation) plus, once the report is
 * closed, a "Last Update by" footer. Renders nothing until the report is closed
 * or an approval note exists.
 */
const EmailNotesCard = ({
  isClosed,
  status,
  notes,
  lastUpdatedBy,
  timeUpdated,
}: EmailNotesCardProps) => {
  if (!(isClosed || notes.approve)) return null;

  return (
    <Paper elevation={3} className={cardStyles.paperOfficerDetailsContainer}>
      {notes.approve ? (
        <NoteSection
          title="Note attached to Approval Email"
          markdown={notes.approve}
          tooltip={EMAIL_NOTE_TOOLTIP}
        />
      ) : (
        status === "approved" && (
          <div className={styles.emptyMessage}>
            No Approval Note Provided.
            <Tooltip title={EMAIL_NOTE_TOOLTIP}>
              <IconButton
                size="small"
                className={styles.infoButton}
              ></IconButton>
            </Tooltip>
          </div>
        )
      )}
      {status === "rejected" &&
        (notes.reject ? (
          <NoteSection
            title="Note attached to Rejection Email"
            markdown={notes.reject}
            tooltip={EMAIL_NOTE_TOOLTIP}
          />
        ) : (
          <div className={styles.emptyMessage}>Reject without any notes.</div>
        ))}
      {status === "escalated" &&
        (notes.escalate ? (
          <NoteSection
            title="Note attached to Escalation Email"
            markdown={notes.escalate}
            tooltip={ESCALATION_NOTE_TOOLTIP}
          />
        ) : (
          <div className={styles.emptyMessage}>Escalate without any notes.</div>
        ))}
      {isClosed && (
        <div className={styles.lastUpdate}>
          Last Update by {lastUpdatedBy?.rank_short} {lastUpdatedBy?.first_name}{" "}
          {lastUpdatedBy?.last_name} ({lastUpdatedBy?.badge_number})
          {timeUpdated ? ` on ${formatDateShort(timeUpdated)}` : ""}
        </div>
      )}
    </Paper>
  );
};

export default EmailNotesCard;
